import {
  DisplayPoint,
  DoubleEndedObstacleLocation,
  ObstacleLocation,
  Point,
  SingleEndedObstacleLocation,
  TripleEndedObstacleLocation,
} from './obstacles';
import { DisplayUnits, Units, inverseOriginTransform } from './dimensions';
import {
  Matrix,
  applyToPoints,
  compose,
  rotateDEG,
  scale,
  translate,
} from 'transformation-matrix';
import { prepareDate, uuid } from './helpers';

import { Map } from './map';
import { Place } from './place';
import { User } from './user';
import { zip } from 'lodash-es';

export class Layout {
  created: string;
  id: string;
  map_id: string;
  map: Map;
  name: string;
  notes: string;
  performed: string;
  place_id: string;
  place: Place;
  tags: string[];
  type = 'layout';
  updated: string;

  // Private cache properties
  private _cachedObstacleLocations: ObstacleLocation[] | null = null;
  private _cachedPoints: DisplayPoint[] | null = null;
  private _lastCacheKey: string | null = null;
  // Add private backing fields for properties that need cache invalidation
  private _x: number;
  private _y: number;
  private _rotation: number;
  private _flip: boolean;
  private _scaleWidth: number;
  private _scaleHeight: number;

  constructor({
    id = null,
    name = '',
    notes = '',
    tags = [],
    created = '',
    updated = '',
    performed = '',
    x = 0,
    y = 0,
    rotation = 0,
    map = null,
    place = null,
    map_id = null,
    place_id = null,
    flip = false,
    scaleWidth = 1,
    scaleHeight = 1,
    ...rest
  }: {
    id?: string;
    name?: string;
    notes?: string;
    tags?: string[];
    created?: string;
    updated?: string;
    performed?: string;
    // location in Place coordinates from top left corner to top left corner of map
    x?: number;
    y?: number;
    rotation?: number; // rotation in degrees about center of map
    map?: Map;
    place?: Place;
    map_id?: string;
    place_id?: string;
    flip?: boolean;
    scaleWidth?: number;
    scaleHeight?: number;
  }) {
    const now = new Date();
    this.id = id ? id : uuid();
    this.map_id = map_id || (map && map.id) || null;
    this.place_id = place_id || (place && place.id) || null;
    this.name = name;
    this.notes = notes;
    this.tags = tags;
    this.created = prepareDate(created, now);
    this.updated = prepareDate(updated, now);
    this.performed = prepareDate(performed, null);

    // Initialize private backing fields
    this._x = x;
    this._y = y;
    this._rotation = rotation;
    this.map = map;
    this.place = place;
    this._flip = flip;
    this._scaleWidth = typeof scaleWidth === 'number' ? scaleWidth : 1;
    this._scaleHeight = typeof scaleHeight === 'number' ? scaleHeight : 1;

    Object.assign(this, rest); // allow old source to edit/persist properties from future versions
  }

  urlId(): string {
    return this.id;
  }

  displayUnits(): DisplayUnits {
    // base off of place's units.
    // TODO start storing in User preferences(?)
    return this.place && this.place.units === Units.meters
      ? DisplayUnits.meters
      : DisplayUnits.feet;
  }

  static scaleValue(map: Map, place: Place): number {
    return Layout.scaledValue(map, place, 1);
  }

  static scaledValue(map: Map, place: Place, value: number): number {
    // convert value in Map Dimension to value in Place Dimension
    if (map.units == place.units) {
      return value;
    }
    if (map.units === Units.feet) {
      // Map in feet and Place is in meters
      return value * 0.3048;
    }
    // Place is in feet and Map is in meters
    return value / 0.3048;
  }

  scaledValue(value: number): number {
    return Layout.scaledValue(this.map, this.place, value);
  }

  scaleTransform(): string {
    return `scale(1/${this.scaledValue(1)})`;
  }

  meters(): boolean {
    return this.place && this.place.units == Units.meters;
  }

  tagsString(): string {
    return this.tags.join(' ');
  }

  searchString(): string {
    return [this.name, this.notes, this.tagsString()].join(' ').toLowerCase();
  }

  set rotate(degrees: number) {
    if (degrees < 0) {
      degrees = 360 + degrees;
    }
    this.rotation = Math.floor(degrees % 360);
  }

  get rotate(): number {
    return this.rotation;
  }

  favorite(user: User): boolean {
    return (
      user &&
      user.settings &&
      user.settings.layoutFavorites.indexOf(this.id) > -1
    );
  }

  static transformedObstacles(
    map: Map,
    place: Place,
    x: number,
    y: number,
    rotation: number,
    flip: boolean,
    scaleWidth = 1,
    scaleHeight = 1,
  ): ObstacleLocation[] {
    const transform = Layout.mapPointToPlacePointTransform(
      map,
      place,
      x,
      y,
      rotation,
      flip,
      scaleWidth,
      scaleHeight,
    );
    return map.obstacleLocations().map((ol) => {
      const newOl = {
        ...ol,
        ...applyToPoints(transform, [ol])[0], // transform x,y only
        r: rotation + (flip ? -1 : 1) * ol.r,
      } as ObstacleLocation;
      return newOl;
    });
  }

  isSaved(): boolean {
    return Boolean(this.name);
  }
  toJSON() {
    // Start with all enumerable properties, then override the private ones
    const result = Object.assign({}, this);
    // Override private fields with their public values
    result.x = this._x;
    result.y = this._y;
    result.rotation = this._rotation;
    result.flip = this._flip;
    result.scaleWidth = this._scaleWidth;
    result.scaleHeight = this._scaleHeight;

    // Remove cache fields that shouldn't be serialized
    delete result._cachedObstacleLocations;
    delete result._cachedPoints;
    delete result._lastCacheKey;
    delete result._x;
    delete result._y;
    delete result._rotation;
    delete result._flip;
    delete result._scaleWidth;
    delete result._scaleHeight;

    return result;
  }

  private _generateCacheKey(): string {
    return `${this._scaleWidth}-${this._scaleHeight}-${this._x}-${this._y}-${this._rotation}-${this._flip}-${this.map?.updated || 'no-update'}`;
  }

  private _invalidateCache(): void {
    this._cachedObstacleLocations = null;
    this._cachedPoints = null;
    this._lastCacheKey = null;
  }

  // Override the existing points getter with caching
  get points(): DisplayPoint[] {
    const currentCacheKey = this._generateCacheKey();

    if (!this._cachedPoints || this._lastCacheKey !== currentCacheKey) {
      if (this._scaleWidth !== 1 || this._scaleHeight !== 1) {
        // Use scaleMap for scaling, then get points without additional scaling
        const scaledLayout = Layout.scaleMap(
          this,
          this._scaleWidth,
          this._scaleHeight,
        );
        const transform = Layout.mapPointToPlacePointTransform(
          scaledLayout.map,
          this.place,
          this._x,
          this._y,
          this._rotation,
          this._flip,
          1, // No additional scaling
          1,
        );
        const points = scaledLayout.map.points();
        this._cachedPoints = zip(
          applyToPoints(transform, points),
          applyToPoints(
            compose(
              inverseOriginTransform(
                this.place.origin,
                this.place.width,
                this.place.height,
              ),
              transform,
            ),
            points,
          ),
        ).map((p: [Point, Point]) => {
          return {
            dx: p[1].x,
            dy: p[1].y,
            ...p[0],
          };
        });
      } else {
        // No scaling needed, use original logic
        const transform = Layout.mapPointToPlacePointTransform(
          this.map,
          this.place,
          this._x,
          this._y,
          this._rotation,
          this._flip,
        );
        const points = this.map.points();
        this._cachedPoints = zip(
          applyToPoints(transform, points),
          applyToPoints(
            compose(
              inverseOriginTransform(
                this.place.origin,
                this.place.width,
                this.place.height,
              ),
              transform,
            ),
            points,
          ),
        ).map((p: [Point, Point]) => {
          return {
            dx: p[1].x,
            dy: p[1].y,
            ...p[0],
          };
        });
      }
      this._lastCacheKey = currentCacheKey;
    }

    return this._cachedPoints;
  }

  // Override the existing obstacleLocations getter with caching
  get obstacleLocations(): ObstacleLocation[] {
    const currentCacheKey = this._generateCacheKey();

    if (
      !this._cachedObstacleLocations ||
      this._lastCacheKey !== currentCacheKey
    ) {
      if (this._scaleWidth !== 1 || this._scaleHeight !== 1) {
        // Use scaleMap for complex obstacle scaling, then transform without additional scaling
        const scaledLayout = Layout.scaleMap(
          this,
          this._scaleWidth,
          this._scaleHeight,
        );
        this._cachedObstacleLocations = Layout.transformedObstacles(
          scaledLayout.map,
          this.place,
          this._x,
          this._y,
          this._rotation,
          this._flip,
          1, // No additional scaling since obstacles are already scaled
          1,
        );
      } else {
        // No scaling needed, use original method
        this._cachedObstacleLocations = Layout.transformedObstacles(
          this.map,
          this.place,
          this._x,
          this._y,
          this._rotation,
          this._flip,
          this._scaleWidth,
          this._scaleHeight,
        );
      }
      this._lastCacheKey = currentCacheKey;
    }

    return this._cachedObstacleLocations;
  }

  // Add getters and setters that invalidate cache
  get x(): number {
    return this._x;
  }

  set x(value: number) {
    if (this._x !== value) {
      this._x = value;
      this._invalidateCache();
    }
  }

  get y(): number {
    return this._y;
  }

  set y(value: number) {
    if (this._y !== value) {
      this._y = value;
      this._invalidateCache();
    }
  }

  get rotation(): number {
    return this._rotation;
  }

  set rotation(value: number) {
    if (this._rotation !== value) {
      this._rotation = value;
      this._invalidateCache();
    }
  }

  get flip(): boolean {
    return this._flip;
  }

  set flip(value: boolean) {
    if (this._flip !== value) {
      this._flip = value;
      this._invalidateCache();
    }
  }

  get scaleWidth(): number {
    return this._scaleWidth;
  }

  set scaleWidth(value: number) {
    if (this._scaleWidth !== value) {
      this._scaleWidth = value;
      this._invalidateCache();
    }
  }

  get scaleHeight(): number {
    return this._scaleHeight;
  }

  set scaleHeight(value: number) {
    if (this._scaleHeight !== value) {
      this._scaleHeight = value;
      this._invalidateCache();
    }
  }

  static scaleMap(
    layout: Layout,
    scaleWidth: number,
    scaleHeight: number,
  ): Layout {
    const newLayout = new Layout({
      ...layout,
      scaleWidth,
      scaleHeight,
    });
    // clone the map and we'll iterate over the obstacleLocations.
    // For SingleEndedObstacleLocations we can scale the x and y coordinates directly and adjust the rotation.
    // For DoubleEndedObstacleLocations we need to scale the x1, y1, x2, y2 coordinates and supply them to the constructor as the start and end attributes.
    // For TripleEndedObstacleLocations we need to scale the x1, y1, x2, y2, x3, y3 coordinates and supply them to the constructor as the start, end, and side attributes.

    newLayout.map = new Map({
      ...layout.map,
      width: layout.map.width * scaleWidth,
      height: layout.map.height * scaleHeight,
      obstacle_locations: layout.map.obstacleLocations().map((ol) => {
        if (ol instanceof SingleEndedObstacleLocation) {
          return new SingleEndedObstacleLocation({
            ...ol,
            x: ol.x * scaleWidth,
            y: ol.y * scaleHeight,
            r: ol.r,
          });
        } else if (ol instanceof DoubleEndedObstacleLocation) {
          return new DoubleEndedObstacleLocation({
            ...ol,
            start: {
              x: ol.x1 * scaleWidth,
              y: ol.y1 * scaleHeight,
            },
            end: {
              x: ol.x2 * scaleWidth,
              y: ol.y2 * scaleHeight,
            },
          });
        } else if (ol instanceof TripleEndedObstacleLocation) {
          // log before and after scaling
          return new TripleEndedObstacleLocation({
            ...ol,
            start: { x: ol.x1 * scaleWidth, y: ol.y1 * scaleHeight },
            end: { x: ol.x2 * scaleWidth, y: ol.y2 * scaleHeight },
            side: { x: ol.x3 * scaleWidth, y: ol.y3 * scaleHeight },
            controls: [],
            units: layout.map.units,
          });
        }
        return ol; // fallback for other types
      }),
    });
    return newLayout;
  }

  // Rotates around scaled center
  static mapToPlaceTransform(
    mapWidth: number,
    mapHeight: number,
    x: number,
    y: number,
    rotation: number,
    flip: boolean,
    scaleValue: number,
    scaleWidth = 1,
    scaleHeight = 1,
  ): Matrix {
    // Calculate center based on scaled dimensions
    const scaledMapWidth = mapWidth * scaleValue * scaleWidth;
    const scaledMapHeight = mapHeight * scaleValue * scaleHeight;
    const centerX = scaledMapWidth / 2;
    const centerY = scaledMapHeight / 2;

    const matrix = compose(
      // 1. Move top-left to (x, y)
      translate(x, y),
      // 2. Move center to origin (using scaled center)
      translate(centerX, centerY),
      // 3. Rotate about center
      rotateDEG(rotation, 0, 0),
      // 4. Apply flip if needed (while at center)
      flip ? scale(-1, 1) : scale(1, 1),
      // 5. Move back to top-left (using scaled center)
      translate(-centerX, -centerY),
      // 6. Scale from top-left
      scale(scaleWidth, scaleHeight),
    );
    return matrix;
  }

  static mapPointToPlacePointTransform(
    map: Map,
    place: Place,
    x: number,
    y: number,
    rotation: number,
    flip: boolean,
    scaleWidth = 1,
    scaleHeight = 1,
  ): Matrix {
    const scaleValue = Layout.scaleValue(map, place);
    const matrix = compose(
      Layout.mapToPlaceTransform(
        map.width,
        map.height,
        x,
        y,
        rotation,
        flip,
        scaleValue,
        scaleWidth,
        scaleHeight,
      ),
      scale(scaleValue),
    );
    return matrix;
  }
}
